import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cloneDeep } from 'lodash'

import Modal from 'components/Modal/Modal'
import Header from 'components/Modal/components/Header/Header'
import Body from 'components/Modal/components/Body/Body'

import StopPanels, {
    useStopPanes,
    VALIDATION_WARNING_TYPES,
} from '../../Blocks/components/Modals/components/StopPanels/StopPanels'
import NavigationListBar from '../../Blocks/components/Controls/NavigationListBar/NavigationListBar'
import useNavigation from '../../Blocks/components/Controls/NavigationListBar/hooks/useNavigation'

import RewardsList from './components/RewardsList/RewardsList'
import Reward from './components/Reward/Reward'

import styles from './RewardsModal.module.scss'

import { WEIGHT_FACTOR } from './components/Reward/constants'

import { RewardModel, validate, getDefaultRewards } from './RewardService'

const RewardsModal = ({ data, onClose, onUpdate, onShowBlockSettingsDialog, payload = {} }) => {
    const { t } = useTranslation()
    const [tmpData, setTmpData] = useState(data.app.rewards || getDefaultRewards())
    const [errors, setErrors] = useState({})

    const {
        isShowQuitAccept,
        onShowQuitAccept,
        validationWarningType,
        setValidationWarningType,
        onClosePanels,
    } = useStopPanes()

    const {
        scrollableContentNodeRef,
        focusedElementId,
        scrollContainerToElement,
        onSetFocusedElementId,
    } = useNavigation('item_')

    const [weightFactorSum, setWeightFactorSum] = useState(0)

    useEffect(() => {
        setWeightFactorSum(tmpData.list.reduce((acc, v) => (acc += WEIGHT_FACTOR[v.weight]), 0))
    }, [tmpData])

    const onSave = async () => {
        const validation = validate(tmpData)

        if (!validation.isHasError) {
            onUpdate({ rewards: validation.data })
            onClose()
            if (!payload.isSkipNextModal) setTimeout(() => onShowBlockSettingsDialog())
        } else {
            setErrors(prevState => ({
                ...prevState,
                ...validation.errors,
            }))
            setValidationWarningType(VALIDATION_WARNING_TYPES.COMMON)
        }
    }

    const onAddReward = () => {
        const newReward = new RewardModel({ index: tmpData.list.length })
        setTmpData(prevState => ({
            ...prevState,
            list: prevState.list.concat(newReward),
        }))
    }
    const onChangeRewardPosition = (currentIndex, newIndex) => {
        const list = cloneDeep(tmpData.list)
        const movingItem = list[currentIndex]
        list[currentIndex] = list[newIndex]
        list[newIndex] = movingItem
        setTmpData(prevState => ({
            ...prevState,
            list,
        }))
    }
    const onCloneReward = index => {
        const sourceReward = tmpData.list[index]
        const clonedReward = new RewardModel(sourceReward)
        setTmpData(prevState => ({
            ...prevState,
            list: prevState.list.concat(clonedReward),
        }))
    }
    const onRemoveReward = index => {
        const list = cloneDeep(tmpData.list)
        list.splice(index, 1)
        setTmpData(prevState => ({
            ...prevState,
            list,
        }))
    }
    const onUpdateReward = (id, index, data) => {
        if (errors.list) {
            const _errors = {}
            Object.keys(data).forEach(key => (_errors[key] = []))
            const listErrors = cloneDeep(errors.list)
            listErrors.set(id, {
                ...listErrors.get(id),
                ..._errors,
            })
            setErrors(prevState => ({
                ...prevState,
                list: listErrors,
            }))
        }
        const list = cloneDeep(tmpData.list)
        list[index] = {
            ...list[index],
            ...data,
        }
        setTmpData(prevState => ({
            ...prevState,
            list,
        }))
    }

    return (
        <Modal closeOnOverlayClick={false} closeOnEsc={false}>
            <Header title={t('Set up rewards')} onClose={onShowQuitAccept} />
            <Body onSave={onSave}>
                <div className={styles.wrapper}>
                    <div className={styles.sidebar}>
                        <NavigationListBar
                            items={tmpData.list}
                            maxLength={100}
                            isRoundedIcon
                            titleOfItem={t('Reward')}
                            focusedItemId={focusedElementId}
                            iconAccessor={RewardModel.previewImageUrl}
                            onClickItem={scrollContainerToElement}
                            onClickAddNew={onAddReward}
                        />
                    </div>
                    <div className={styles.content}>
                        <RewardsList scrollableContentNodeRef={scrollableContentNodeRef}>
                            {tmpData.list.map((reward, index) => (
                                <Reward
                                    key={reward.id}
                                    list={tmpData.list}
                                    errors={errors?.list?.get(reward.id)}
                                    data={reward}
                                    index={index}
                                    onHandleVisibility={(inView, entry) => {
                                        if (inView) onSetFocusedElementId(entry.target)
                                    }}
                                    weightFactorSum={weightFactorSum}
                                    onUpdate={data => onUpdateReward(reward.id, index, data)}
                                    onChangePosition={onChangeRewardPosition}
                                    onClone={onCloneReward}
                                    onRemove={onRemoveReward}
                                />
                            ))}
                        </RewardsList>
                    </div>
                </div>
            </Body>

            <StopPanels
                isShowQuitAccept={isShowQuitAccept}
                validationWarningType={validationWarningType}
                onBack={onClosePanels}
                onQuit={onClose}
            />
        </Modal>
    )
}

export default RewardsModal
